class ShopTheLookDots extends HTMLElement {
  mobileBreakpoint = 1024;

  connectedCallback() {
    this.dots = this.querySelectorAll(".shop-the-look__dot");
    this.closeButtons = this.querySelectorAll(".shop-the-look__popup-close");

    this.drawer = document.querySelector("[data-quick-add-drawer]");
    this.drawerOverlay = document.querySelector("[data-quick-add-overlay]");
    this.drawerContent = document.querySelector("[data-quick-add-content]");
    this.drawerClose = document.querySelector("[data-quick-add-close]");

    this.initMobileSlider();
    this.bindDots();
    this.bindPopupClose();
    this.bindQuickAdd();
    this.bindDrawer();

    document.addEventListener("on:cart:add", () => {
      this.closeDrawer();
    });
  }

  getSectionRoot() {
    return this.closest('[id^="shopify-section-"]') || this;
  }

  initMobileSlider() {
    const sectionRoot = this.getSectionRoot();
    const sliderRoot = sectionRoot.querySelector('[data-grid-slide="true"]');

    if (
      !sliderRoot ||
      sliderRoot.dataset.mobileSliderInitialized === "true" ||
      typeof Swiper === "undefined"
    ) {
      return;
    }

    const container = sliderRoot.querySelector(".swiper-container");
    const wrapper = sliderRoot.querySelector(".swiper-wrapper");

    if (!container || !wrapper) return;

    const prevEls = sliderRoot.querySelectorAll(".shop-the-look-mobile-arrow--prev");
    const nextEls = sliderRoot.querySelectorAll(".shop-the-look-mobile-arrow--next");
    const mobileBreakpoint = window.matchMedia(
      `(max-width: ${this.mobileBreakpoint}px)`
    );

    prevEls.forEach((button) => {
      button.addEventListener("click", () => {
        container.swiper?.slidePrev();
      });
    });

    nextEls.forEach((button) => {
      button.addEventListener("click", () => {
        container.swiper?.slideNext();
      });
    });

    const create = () => {
      if (container.swiper) return;

      new Swiper(container, {
        loop: false,
        slidesPerView: 1,
        spaceBetween: 16,
        autoHeight: true,
        allowTouchMove: true,
      });
    };

    const destroy = () => {
      if (container.swiper) {
        container.swiper.destroy(true, false);
      }

      wrapper.style.removeProperty("transform");
      wrapper.style.removeProperty("transition-duration");
      wrapper.style.removeProperty("height");
      container.style.removeProperty("height");

      sectionRoot.querySelectorAll(".swiper-slide").forEach((slide) => {
        slide.style.removeProperty("width");
        slide.style.removeProperty("margin-right");
        slide.style.removeProperty("margin-left");
        slide.style.removeProperty("transform");
        slide.style.removeProperty("height");
      });
    };

    const sync = () => {
      if (mobileBreakpoint.matches) {
        create();
        container.swiper?.update();
      } else {
        destroy();
      }
    };

    const handleBreakpointChange = () => {
      window.requestAnimationFrame(sync);
    };

    if (mobileBreakpoint.addEventListener) {
      mobileBreakpoint.addEventListener("change", handleBreakpointChange);
    } else if (mobileBreakpoint.addListener) {
      mobileBreakpoint.addListener(handleBreakpointChange);
    }

    sliderRoot.dataset.mobileSliderInitialized = "true";
    sync();
  }

  bindDots() {
    this.dots.forEach((dot) => {
      dot.addEventListener("click", (event) => {
        this.onDotClick(event.currentTarget);
      });
    });
  }

  bindPopupClose() {
    this.closeButtons.forEach((btn) => {
      btn.addEventListener("click", (event) => {
        event.stopPropagation();
        this.onPopupClose(event.currentTarget);
      });
    });
  }

  onDotClick(dot) {
    const isMobile = window.matchMedia(
      `(max-width: ${this.mobileBreakpoint}px)`
    ).matches;
    const hotspotIndex = dot.dataset.hotspotIndex;
    const handle = dot.dataset.productHandle;
    const productImage = dot.dataset.productImage;
    const sectionRoot = this.getSectionRoot();

    if (isMobile && hotspotIndex) {
      const imageBlock = dot.closest(".shop-the-look-image") || this;
      const cardsWrapper = imageBlock.querySelector("[data-hotspot-cards]");
      const targetCard = cardsWrapper?.querySelector(
        `[data-hotspot-index="${hotspotIndex}"]`
      );
      const isOpen = dot.getAttribute("aria-expanded") === "true";

      this.closeAllPopups(sectionRoot);

      if (!cardsWrapper || !targetCard || isOpen) {
        this.updateMobileSliderHeight(sectionRoot);
        return;
      }

      cardsWrapper.hidden = false;
      cardsWrapper.classList.add("is-active");
      targetCard.classList.add("is-active");
      dot.setAttribute("aria-expanded", "true");
      this.updateMobileSliderHeight(sectionRoot);

      return;
    }

    this.closeAllPopups(sectionRoot);
    this.openQuickAddDrawer(handle, productImage);
  }

  onPopupClose(button) {
    const hotspot = button.closest(".shop-the-look__hotspot");
    hotspot.querySelector(".shop-the-look__popup").hidden = true;
    hotspot
      .querySelector(".shop-the-look__dot")
      .setAttribute("aria-expanded", "false");
  }

  closeAllPopups(root = this) {
    root.querySelectorAll(".shop-the-look__popup").forEach((popup) => {
      popup.hidden = true;
    });

    root.querySelectorAll(".shop-the-look__dot").forEach((dot) => {
      dot.setAttribute("aria-expanded", "false");
    });

    root.querySelectorAll("[data-hotspot-cards]").forEach((cardsWrapper) => {
      cardsWrapper.hidden = true;
      cardsWrapper.classList.remove("is-active");
    });

    root.querySelectorAll(".shop-the-look__card").forEach((card) => {
      card.classList.remove("is-active");
    });
  }

  updateMobileSliderHeight(root = this) {
    const slider = root.querySelector(".swiper-container")?.swiper;
    if (!slider) return;

    requestAnimationFrame(() => {
      slider.updateAutoHeight(250);
      slider.update();
    });
  }

  bindQuickAdd() {
    this.quickAddButtons = document.querySelectorAll(
      ".shop-the-look__card .btn[data-product-handle], .shop-the-look__popup .btn[data-product-handle]"
    );

    this.quickAddButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.onQuickAddClick(button);
      });
    });
  }

  onQuickAddClick(button) {
    const handle = button.dataset.productHandle;
    const productImage = button.dataset.productImage;
    const singleVariantId = button.dataset.singleVariantId;
    const hasMultipleVariants = button.dataset.hasMultipleVariants === "true";

    if (!handle) return;

    if (singleVariantId && !hasMultipleVariants) {
      this.addSingleVariantToCart(singleVariantId);
      return;
    }

    this.openQuickAddDrawer(handle, productImage);
  }

  openQuickAddDrawer(handle, productImage) {
    if (!handle || !this.drawer || !this.drawerContent || !this.drawerOverlay) {
      return;
    }

    if (productImage) {
      const preloadImage = new Image();
      preloadImage.fetchPriority = "high";
      preloadImage.decoding = "async";
      preloadImage.src = productImage;
    }

    this.openDrawer();

    fetch(`/products/${handle}?view=quick-add`)
      .then((response) => {
        if (!response.ok) throw new Error("Quick add view not found");
        return response.text();
      })
      .then((html) => {
        this.drawerContent.innerHTML = html;
        this.bindQuickAddVariantUI();
      })
      .catch(() => {
        this.drawerContent.innerHTML =
          "<p>There was an error loading this product.</p>";
      });
  }

  bindQuickAddVariantUI() {
    const form = this.drawerContent.querySelector("form.quick-add-form");
    const variantPicker = this.drawerContent.querySelector("variant-picker");
    const idInput = form?.querySelector('input[name="id"]');
    const submitBtn = form?.querySelector(".add-to-cart");
    const mediaImg = this.drawerContent.querySelector(".quick-add-media img");

    if (!form || !variantPicker || !idInput) return;

    let variantsData = null;
    const jsonScript = variantPicker.querySelector(
      'script[type="application/json"]'
    );

    if (jsonScript) {
      try {
        const parsed = JSON.parse(jsonScript.textContent);
        variantsData = parsed.variants || [];
      } catch (e) {
        variantsData = null;
      }
    }

    if (!variantsData || !variantsData.length) return;

    const optionSelectors = variantPicker.querySelectorAll(".option-selector");

    const getSelectedOptionsFromUI = () => {
      return Array.from(optionSelectors).map((selector) => {
        const type = selector.dataset.selectorType;

        if (type === "listed") {
          const checked = selector.querySelector(".opt-btn:checked");
          return checked ? checked.value : null;
        }

        if (type === "dropdown") {
          const select = selector.querySelector("select");
          if (select && select.value !== "") return select.value;

          const active = selector.querySelector("[data-custom-select-value]");
          if (active && active.textContent.trim() !== "")
            return active.textContent.trim();

          const selected = selector.querySelector(
            '.custom-select__option[aria-selected="true"]'
          );
          return selected && selected.dataset.value !== ""
            ? selected.dataset.value
            : null;
        }

        return null;
      });
    };

    const applyVariantSelectionToUI = (variant) => {
      if (!variant) return;

      variant.options.forEach((optValue, idx) => {
        const selector = optionSelectors[idx];
        if (!selector) return;

        const type = selector.dataset.selectorType;

        if (type === "listed") {
          const input = selector.querySelector(
            `.opt-btn[value="${CSS.escape(optValue)}"]`
          );
          if (input) {
            input.checked = true;
            input.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }

        if (type === "dropdown") {
          const select = selector.querySelector("select");
          if (select) {
            select.value = optValue;
            select.dispatchEvent(new Event("change", { bubbles: true }));
            return;
          }

          const customOpt = selector.querySelector(
            `.custom-select__option[data-value="${CSS.escape(optValue)}"]`
          );
          if (customOpt) {
            const customSelect = customOpt.closest("custom-select");
            if (customSelect?.selectOption) {
              customSelect.selectOption(customOpt);
            } else {
              selector
                .querySelectorAll(".custom-select__option")
                .forEach((o) => o.setAttribute("aria-selected", "false"));
              customOpt.setAttribute("aria-selected", "true");
              const valueEl = selector.querySelector(
                "[data-custom-select-value]"
              );
              if (valueEl) valueEl.textContent = optValue;
            }
            customOpt.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      });
    };

    const updateOptionAvailability = (optionEl, exists, available) => {
      const unavailableText = exists
        ? theme.strings.noStock
        : theme.strings.noVariant;

      if (optionEl.matches("input")) {
        const label = optionEl.nextElementSibling;
        if (label) {
          label.classList.toggle("is-unavailable", !available);
          label.classList.toggle("is-nonexistent", !exists);
          if (!available) label.title = unavailableText;
          else label.removeAttribute("title");
        }
        optionEl.disabled = !exists;
        return;
      }

      optionEl.classList.toggle("is-unavailable", !available);
      optionEl.classList.toggle("is-nonexistent", !exists);

      if (optionEl.classList.contains("custom-select__option")) {
        let em = optionEl.querySelector("em");
        if (available) {
          if (em) em.hidden = true;
        } else {
          if (!em) {
            optionEl.insertAdjacentHTML(
              "beforeend",
              ` <em class="pointer-events-none">${unavailableText}</em>`
            );
            em = optionEl.querySelector("em");
          }
          em.hidden = false;
          em.textContent = unavailableText;
        }
      }
    };

    const updateAvailabilityUI = () => {
      const selectedOptions = getSelectedOptionsFromUI();

      optionSelectors.forEach((selector, selectorIndex) => {
        const type = selector.dataset.selectorType;

        if (type === "listed") {
          const inputs = selector.querySelectorAll(".opt-btn");
          inputs.forEach((input) => {
            const optionValue = input.value;

            let variantsExist = false;
            let variantsAvailable = false;

            variantsData.forEach((v) => {
              let match = true;
              for (let i = 0; i < v.options.length; i += 1) {
                if (i === selectorIndex) continue;
                if (
                  selectedOptions[i] !== null &&
                  v.options[i] !== selectedOptions[i]
                ) {
                  match = false;
                  break;
                }
              }

              if (match && v.options[selectorIndex] === optionValue) {
                variantsExist = true;
                if (v.available) variantsAvailable = true;
              }
            });

            updateOptionAvailability(input, variantsExist, variantsAvailable);
          });
        }

        if (type === "dropdown") {
          const dropdownOptions = selector.querySelectorAll(
            ".custom-select__option[data-value]:not([data-value=''])"
          );

          dropdownOptions.forEach((opt) => {
            const optionValue = opt.dataset.value;

            let variantsExist = false;
            let variantsAvailable = false;

            variantsData.forEach((v) => {
              let match = true;
              for (let i = 0; i < v.options.length; i += 1) {
                if (i === selectorIndex) continue;
                if (
                  selectedOptions[i] !== null &&
                  v.options[i] !== selectedOptions[i]
                ) {
                  match = false;
                  break;
                }
              }

              if (match && v.options[selectorIndex] === optionValue) {
                variantsExist = true;
                if (v.available) variantsAvailable = true;
              }
            });

            updateOptionAvailability(opt, variantsExist, variantsAvailable);
          });
        }
      });
    };

    const updateFromOptions = () => {
      const selectedOptions = getSelectedOptionsFromUI();

      const matchingVariant = variantsData.find((variant) =>
        variant.options.every(
          (value, index) => value === selectedOptions[index]
        )
      );

      if (!matchingVariant) {
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = theme.strings.noVariant;
        }
        return;
      }

      idInput.value = matchingVariant.id;
      idInput.dispatchEvent(new Event("change", { bubbles: true }));

      if (submitBtn) {
        submitBtn.disabled = !matchingVariant.available;
        submitBtn.textContent = matchingVariant.available
          ? theme.strings.addToCart || "Add to cart"
          : theme.strings.noStock;
      }

      if (mediaImg) {
        const media =
          matchingVariant.featured_media || matchingVariant.featured_image;
        const imageObject = media && (media.preview_image || media);
        if (imageObject?.src) {
          mediaImg.src = imageObject.src;
          mediaImg.srcset = imageObject.src;
          if (imageObject.alt) mediaImg.alt = imageObject.alt;
        }
      }

      updateAvailabilityUI();
    };

    optionSelectors.forEach((selector) => {
      selector.querySelectorAll(".opt-btn").forEach((radio) => {
        radio.addEventListener("change", updateFromOptions);
      });

      const select = selector.querySelector("select");
      if (select) select.addEventListener("change", updateFromOptions);

      selector.querySelectorAll(".custom-select__option").forEach((opt) => {
        opt.addEventListener("click", updateFromOptions);
      });
    });

    const firstAvailable =
      variantsData.find((v) => v.available) || variantsData[0];
    applyVariantSelectionToUI(firstAvailable);

    updateFromOptions();
  }

  addSingleVariantToCart(variantId) {
    const quantity = 1;

    const sectionsToUpdate = ["page-header", "cart-drawer"]
      .map((sel) => document.querySelector(sel))
      .filter((el) => el);

    const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", quantity);
    formData.append("sections_url", window.location.pathname);
    formData.append("sections", sectionIds.join(","));

    fetch(theme.routes.cartAdd, {
      method: "POST",
      headers: {
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        sectionsToUpdate.forEach((el) => {
          el.updateFromCartChange(data.sections[el.dataset.sectionId]);
        });

        if (theme.settings.afterAddToCart === "drawer") {
          const cartDrawer = document.querySelector(".js-cart-drawer");
          if (cartDrawer) cartDrawer.open();
        } else if (theme.settings.afterAddToCart === "page") {
          window.location.href = theme.routes.cart;
        }

        this.closeAllPopups();
      })
      .catch(() => {});
  }

  /* ---------- Drawer helpers ---------- */

  bindDrawer() {
    if (this.drawerClose) {
      this.drawerClose.addEventListener("click", () => this.closeDrawer());
    }
    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener("click", () => this.closeDrawer());
    }
  }

  openDrawer() {
    if (!this.drawer || !this.drawerOverlay) return;

    this.drawer.hidden = false;
    this.drawerOverlay.hidden = false;

    requestAnimationFrame(() => {
      this.drawer.classList.add("is-open");
    });
  }

  closeDrawer() {
    if (!this.drawer || !this.drawerOverlay) return;

    this.drawer.classList.remove("is-open");
    this.drawerOverlay.hidden = true;

    setTimeout(() => {
      this.drawer.hidden = true;
    }, 300);
  }
}

customElements.define("shop-the-look-dots", ShopTheLookDots);
